import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import AdminLayout from "../../components/AdminLayout";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchUsers();
  }, [adminToken, page]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users?page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const handleBanUser = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/ban`,
        { action: "ban", reason: "Violation of community guidelines" },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("User banned successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Error banning user");
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/unban`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("User unbanned successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Error unbanning user");
    }
  };

  const handleVerifyUser = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/verify`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("User verified successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Error verifying user");
    }
  };

  const filteredUsers = users.filter(user => 
    user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout title="Users" subtitle="Manage and moderate user accounts">
      {/* Search */}
      <div className="mb-6">
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full sm:max-w-xs bg-slate-800 border-slate-600 text-white placeholder:text-slate-500"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-rose-500">Loading users...</div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {filteredUsers.map((user) => (
              <div key={user.id} className="bg-slate-800 rounded-xl border border-slate-700 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400 font-medium">
                      {user.first_name?.[0] || "?"}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.first_name} {user.last_name}</p>
                      <p className="text-slate-400 text-sm truncate max-w-[180px]">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                        •••
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                      <DropdownMenuItem 
                        onClick={() => navigate(`/profile/${user.id}`)}
                        className="text-slate-300 hover:text-white hover:bg-slate-700"
                      >
                        👁 View Profile
                      </DropdownMenuItem>
                      {!user.is_id_verified && (
                        <DropdownMenuItem 
                          onClick={() => handleVerifyUser(user.id)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700"
                        >
                          ✓ Verify User
                        </DropdownMenuItem>
                      )}
                      {user.is_banned ? (
                        <DropdownMenuItem 
                          onClick={() => handleUnbanUser(user.id)}
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700"
                        >
                          ↩ Unban User
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem 
                          onClick={() => handleBanUser(user.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-slate-700"
                        >
                          🚫 Ban User
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    user.is_banned 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {user.is_banned ? "Banned" : "Active"}
                  </span>
                  {user.is_id_verified && (
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                      Verified
                    </span>
                  )}
                  <span className="text-slate-500 text-xs">
                    Joined {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">User</th>
                    <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Email</th>
                    <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Status</th>
                    <th className="text-left text-slate-400 font-medium px-6 py-4 text-sm">Joined</th>
                    <th className="text-right text-slate-400 font-medium px-6 py-4 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400 font-medium">
                            {user.first_name?.[0] || "?"}
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.first_name} {user.last_name}</p>
                            <p className="text-slate-500 text-sm capitalize">{user.gender}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-300">{user.email}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            user.is_banned 
                              ? 'bg-red-500/20 text-red-400' 
                              : 'bg-emerald-500/20 text-emerald-400'
                          }`}>
                            {user.is_banned ? "Banned" : "Active"}
                          </span>
                          {user.is_id_verified && (
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                              Verified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-400 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                              •••
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem 
                              onClick={() => navigate(`/profile/${user.id}`)}
                              className="text-slate-300 hover:text-white hover:bg-slate-700"
                            >
                              👁 View Profile
                            </DropdownMenuItem>
                            {!user.is_id_verified && (
                              <DropdownMenuItem 
                                onClick={() => handleVerifyUser(user.id)}
                                className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700"
                              >
                                ✓ Verify User
                              </DropdownMenuItem>
                            )}
                            {user.is_banned ? (
                              <DropdownMenuItem 
                                onClick={() => handleUnbanUser(user.id)}
                                className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-700"
                              >
                                ↩ Unban User
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => handleBanUser(user.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-slate-700"
                              >
                                🚫 Ban User
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-400"
              >
                ←
              </Button>
              <span className="px-4 py-2 text-slate-400 text-sm">
                {page} / {totalPages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-400"
              >
                →
              </Button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminUsersPage;
